import React from 'react';

import Data from 'data';
import Store from 'store';

import { showItemLevel } from 'tooltip/ItemTooltip';
import { OverlayContext } from 'ui/utils/OverlayManager';
import { faBan } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Dialog from 'ui/dialog';
import Tooltip from 'ui/tooltip';

export const displayStats = ['criticalhit', 'specialty', 'oppression', 'rapidity', 'endurance', 'mastery'];

export function builderHasPaperdoll(builder) {
  if (builder.category === 'arena' || builder.category === 'inferno') return false;
  return builder.engraving1 || builder.engraving2 || (builder.items && Object.values(builder.items).some(Boolean));
}

export function builderHasStats(builder) {
  return builderHasPaperdoll(builder) || (builder.stats && Object.values(builder.stats).some(Boolean));
}

export function builderSelectStats(builder, extra) {
  if (builderHasPaperdoll(builder)) {
    const stats = {};
    if (!builder.items) return stats;
    for (const item of Object.values(builder.items)) {
      if (!item) continue;
      if (item.quality) {
        for (const [key, value] of Object.entries(item.quality)) {
          if (!stats[key]) stats[key] = {value: 0};
          stats[key].value += value;
        }
      }
      if (item.bracelet) {
        for (const line of item.bracelet) {
          for (const [key, value] of Object.entries(line)) {
            if (Data.itemStats[key]) {
              if (!stats[key]) stats[key] = {value: 0};
              stats[key].value += value;
            }
          }
        }
      }
    }
    if (extra) {
      function add(key, potions, books) {
        if (!stats[key]) stats[key] = {value: 0};
        stats[key].potions = potions;
        stats[key].books = books;
        stats[key].value += potions + books;
      }
      add('criticalhit', 20, 29);
      add('specialty', 24, 25);
      add('oppression', 24, 28);
      add('rapidity', 24, 26);
      add('endurance', 20, 21);
      add('mastery', 24, 26);
      // Vykas: 2 specialty, 3 oppression, 2 rapidity
      // Kakul Saydon: 2 rapidity, 5 endurance, 3 mastery
      // Brelshaza: 3 specialty, 3 endurance
      // Hanumatan: 2 specialty, 2 criticalhit
      // Thaemine: 3 criticalhit, 2 endurance, 3 mastery
      const max = displayStats.reduce((a, b) => stats[a].value > stats[b].value ? a : b, displayStats[0]);
      stats[max].value = Math.floor(stats[max].value * 1.1);
    }
    return stats;
  } else {
    const stats = {};
    if (builder.stats) {
      for (const [key, value] of Object.entries(builder.stats)) {
        stats[key] = {value};
      }
    }
    return stats;
  }
}

export function builderSelectItemLevel(builder) {
  if (!builderHasPaperdoll(builder) || !builder.items) return 0;
  let sum = 0;
  for (const item of Object.values(builder.items)) {
    const data = Data.items[item?.id];
    if (!data) continue;
    const upgrade = Data.itemQuality[`${data.quality}#${100 + item.upgrade}`];
    if (showItemLevel(data.category) && data.ilevel) {
      sum += data.ilevel + (upgrade?.level || 0);
    }
  }
  return sum / 6;
}

export function builderHasEngravings(builder) {
  if (builder.category === 'arena') return false;
  return Object.values(builderSelectEngravings(builder)).some(Boolean);
}

export function builderSelectEngravings(builder) {
  if (builderHasPaperdoll(builder)) {
    const sum = {};
    if (builder.items) for (const item of Object.values(builder.items)) {
      if (!item?.engraving) continue;
      for (const [id, points] of Object.entries(item.engraving)) {
        sum[id] = (sum[id] || 0) + points;
      }
    }
    for (const {id, points} of [builder.engraving1, builder.engraving2].filter(Boolean)) {
      sum[id] = (sum[id] || 0) + points;
    }
    const result = {};
    for (const [id, points] of Object.entries(sum)) {
      if (points >= 5) {
        result[id] = Math.min(3, Math.floor(points / 5));
      }
    }
    return result;
  } else if (builder.category === 'inferno') {
    const result = {};
    if (builder.engravings) {
      for (const [id, value] of Object.entries(builder.engravings)) {
        if (value && Data.engraving[id] && !Data.engraving[id].penalty) {
          result[id] = 3;
        }
      }
    }
    return result;
  } else {
    return builder.engravings || {};
  }
}

export function builderHasSkills(builder) {
  return builder.skills && Object.values(builder.skills).some(Boolean);
}

function skillInGroup(skill, id) {
  const group = Data.skillGroup[id];
  if (group) return group.skills.includes(skill);
  else return id === skill;
}

export function builderSelectSkill(builder, id) {
  id = parseInt(id);
  const result = {id};
  if (!Data.skills[id]) return result;

  if (builder.skills && builder.skills[id]) {
    Object.assign(result, builder.skills[id]);
  }

  const tabs = Data.skillTabs[builder.class];
  if (tabs) {
    const tab = tabs.find(tab => tab.types && tab.types.includes(Data.skills[id].category));
    if (tab && tab.limit === 0) {
      result.level = 1;
    }
  }
  if (builder.category === 'arena' && result.level > 10) {
    result.level = 10;
  }

  if (result.tripods) {
    const tripods = {};
    for (const [index, value] of Object.entries(result.tripods)) {
      const row = Math.floor((parseInt(index) - 1) / 3);
      if (result.level < 4 + row * 3) continue;
      if (builder.category === 'arena') {
        tripods[index] = 1;
      } else if (builder.category === 'inferno') {
        tripods[index] = value > 1 ? 5 : 1;
      } else {
        tripods[index] = value;
      }
    }
    // if (builderHasPaperdoll(builder)) {
    //   for (const index in tripods) tripods[index] = 1;
    //   if (builder.items) for (const item of Object.values(builder.items)) {
    //     if (!item?.tripod) continue;
    //     for (const index in tripods) {
    //       tripods[index] = Math.max(tripods[index], 1 + (item.tripod[`${id}#${index}`] || 0));
    //     }
    //   }
    // }
    result.tripods = tripods;
  }

  if (builder.gems) {
    const gems = [];
    for (const gem of builder.gems) {
      if (gem.gem1 && skillInGroup(id, gem.id1)) gems.push([gem.gem1, gem.id1]);
      if (gem.gem2 && skillInGroup(id, gem.id2)) gems.push([gem.gem2, gem.id2]);
    }
    if (gems.length) result.gems = gems;
  }

  return result;
}

export function builderTripodLevel(builder, id, index) {
  index = parseInt(index);
  const skill = builder.skills?.[id];
  if (!skill || skill.level < 4 + Math.floor((index - 1) / 3) * 3) return 0;
  if (!skill.tripods || !skill.tripods[index]) return 0;
  if (false && builderHasPaperdoll(builder)) {
    let result = 1;
    if (builder.items) for (const item of Object.values(builder.items)) {
      if (!item?.tripod) continue;
      result = Math.max(result, 1 + (item.tripod[`${id}#${index}`] || 0));
    }
    return result;
  } else if (builder.category === 'arena') {
    return 1;
  } else if (builder.category === 'inferno') {
    return skill.tripods[index] > 1 ? 5 : 1;
  } else {
    return skill.tripods[index];
  }
}

export function builderHasGems(builder) {
  if (builder.category === 'arena') return false;
  return builder.gems?.some(gem => gem.gem1 || gem.gem2);
}

export function builderHasCards(builder) {
  if (builder.category === 'arena' || builder.category === 'inferno') return false;
  return builder.cards?.some(card => card && Data.cards[card.id]);
}

export function builderHasSetBonus(builder) {
  return builder.category === 'inferno' && !!builder.setBonus;
}

export function builderIsEmpty(builder) {
  return !builderHasPaperdoll(builder) &&
         !builderHasStats(builder) &&
         !builderHasEngravings(builder) &&
         !builderHasSkills(builder) &&
         !builderHasGems(builder) &&
         !builderHasCards(builder) &&
         !builderHasSetBonus(builder);
}

function ClearConfirmation({ onResolve, onClose }) {
  return (
    <Dialog title={`Clear block`} modal onClose={onClose}>
      <Dialog.Content>
        <p>{`Clear block contents?`}</p>
      </Dialog.Content>
      <Dialog.Footer>
        <button onClick={() => onResolve(true)}>{`Yes`}</button>
        <button onClick={() => onResolve(false)}>{`No`}</button>
      </Dialog.Footer>
    </Dialog>
  );
}

export default function BlockClearButton({ onClear }) {
  const dispatch = Store.useDispatch();
  const overlays = React.useContext(OverlayContext);
  const onClick = React.useCallback(async () => {
    if (!await overlays.async(<ClearConfirmation/>)) return;
    dispatch.setBuilder(builder => onClear(builder));
  }, [dispatch, overlays, onClear]);
  return (
    <Tooltip tooltip={`Clear block`}>
      <FontAwesomeIcon className="lap-button" icon={faBan} onClick={onClick}/>
    </Tooltip>
  );
}
